/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { current } from "@reduxjs/toolkit";
import type { Draft } from "@reduxjs/toolkit";
import { DateTime } from "@tivocorp/data-domain-lib";
import type { TChannel, TGridGuideRow } from "@tivocorp/data-domain-lib";

import {
  GUIDE_HIGHLIGHT_ROW_MIN,
  GUIDE_HIGHLIGHT_ROW_MAX,
  GUIDE_WINDOW_DURATION_MINUTES,
  GUIDE_TIME_SLOT_INTERVAL_MINUTES,
  GUIDE_VISIBLE_ROW_COUNT,
  GUIDE_SHIFT_MINUTES,
  GUIDE_MAX_FUTURE_DAYS,
  GUIDE_TOTAL_CACHED_CHANNELS,
  GUIDE_CHANNEL_BUFFER_TOP,
  GUIDE_CHANNEL_BUFFER_BOTTOM,
  GUIDE_PREFETCH_CH_DOWN_COUNT,
  GUIDE_PREFETCH_CH_UP_COUNT,
  GUIDE_PREFETCH_TIME_THRESHOLD_SLOTS,
  SECONDS_IN_A_MINUTE,
  MILLISECONDS_IN_A_SECOND
} from "../../config";
import { GuideCache } from "./guideCache";
import { getGuideFavoritesFallbackFilter } from "./guideFilters";
import type {
  GuideCellView,
  GuidePrefetchTarget,
  GuideState,
  GuideVisibleChannelRow,
  GuideVisibleRowView,
  TGuideFilterItem
} from "./guide.types";
import { GuideTimeWindow } from "./guide.types";

const MINUTES_PER_DAY: number = 24 * 60;

/**
 * Calculates the guide start time used by the header and row clipping.
 *
 * @param state Current guide state snapshot.
 * @returns Past-adjusted guide start time when in replay mode.
 */
export const getGuideStartTime = (state: GuideState): DateTime | null => {
  if (state.currentWindowStartTime === null) {
    return null;
  }

  if (state.isPastGuide) {
    return state.currentWindowStartTime.subtractMinutes(
      GUIDE_WINDOW_DURATION_MINUTES - GUIDE_SHIFT_MINUTES
    );
  }

  return state.currentWindowStartTime;
};

/**
 * Resolves the Favorites filter's id from the currently loaded filter items,
 * falling back to the client-side fallback constant when items haven't
 * loaded yet or none are flagged as favorite.
 *
 * @param state Current guide state snapshot (read-only or Draft).
 * @returns The Favorites filter's id.
 */
export const getFavoriteFilterId = (
  state: GuideState | Draft<GuideState>
): string => {
  const favoritesFilter: TGuideFilterItem | undefined = state.filterItems.find(
    (item: TGuideFilterItem): boolean => item.isFavorite
  );
  return favoritesFilter !== undefined
    ? favoritesFilter.id
    : getGuideFavoritesFallbackFilter().id;
};

/**
 * True when a filter item can't be selected — currently only Favorites while
 * the user has no favorite channels.
 */
export const isFilterItemDisabled = (
  state: GuideState | Draft<GuideState>,
  item: TGuideFilterItem
): boolean => item.isFavorite && !state.hasFavoriteChannels;

/**
 * True when at least one sidebar item can be focused/selected. False e.g. when
 * the filter list failed to load and only the disabled Favorites fallback is
 * shown.
 */
export const hasSelectableFilterItem = (
  state: GuideState | Draft<GuideState>
): boolean =>
  state.filterItems.some(
    (item: TGuideFilterItem): boolean => !isFilterItemDisabled(state, item)
  );

/**
 * Nearest selectable sidebar index to `index` (preferring the lower index on
 * ties), or `index` itself when no item is selectable.
 */
export const nearestEnabledFilterIndex = (
  state: GuideState | Draft<GuideState>,
  index: number
): number => {
  for (let offset: number = 0; offset < state.filterItems.length; offset++) {
    for (const candidate of [index - offset, index + offset]) {
      const item: TGuideFilterItem | undefined = state.filterItems[candidate];
      if (item !== undefined && !isFilterItemDisabled(state, item)) {
        return candidate;
      }
    }
  }
  return index;
};

/**
 * Next selectable sidebar index from `index` in `direction`, or `index` when
 * there is none in that direction.
 */
export const stepEnabledFilterIndex = (
  state: GuideState | Draft<GuideState>,
  index: number,
  direction: 1 | -1
): number => {
  for (
    let i: number = index + direction;
    i >= 0 && i < state.filterItems.length;
    i += direction
  ) {
    if (!isFilterItemDisabled(state, state.filterItems[i])) {
      return i;
    }
  }
  return index;
};

/**
 * True when the Favorites filter is part of the current selection.
 *
 * @param state Current guide state snapshot (read-only or Draft).
 */
export const isFavoritesFilterSelected = (
  state: GuideState | Draft<GuideState>
): boolean => state.selectedFilterIds.includes(getFavoriteFilterId(state));

/**
 * Resolves the channel/station id key `matchesActiveGuideFilters` indexes by — `channelId`, falling
 * back to `stationId` when it's undefined.
 *
 * @param channel Channel to key.
 * @returns The channel's matching key, or undefined when neither id is set.
 */
const getChannelFilterKey = (channel: TChannel): string | undefined =>
  channel.channelId !== undefined ? channel.channelId : channel.stationId;

/**
 * TEMPORARY workaround for a known id-space mismatch: the v3 channels API
 * correctly supports global channels, so a global channel's `channelId`
 * comes back prefixed "tivo:gc." (e.g. "tivo:gc.633") — but
 * `/v2/guideChannelIds` does NOT support global channels, so it returns the
 * same channel prefixed "tivo:ch." (e.g. "tivo:ch.633") instead — same
 * numeric id, different namespace prefix. Comparing only the trailing
 * number sidesteps the prefix mismatch until the backend aligns the two id
 * spaces.
 *
 * Only ids shaped like "<prefix>.<digits>" (a literal dot immediately
 * before the trailing digits) are normalized this way; anything else
 * (including ids with no dot at all) is returned unchanged and falls back
 * to exact-string matching, so this can't accidentally collapse unrelated
 * ids that merely happen to end in the same digit.
 *
 * @param id Raw channel/station/category id.
 * @returns The trailing "<prefix>.<digits>" segment's digits, or `id`
 * unchanged when it isn't shaped that way.
 */
const normalizeChannelFilterId = (id: string): string => {
  const match: RegExpExecArray | null = /\.(\d+)$/.exec(id);
  return match !== null ? match[1] : id;
};

/**
 * True when `channel` is visible under the currently active guide filter
 * selection (favorite channels ∪ resolved category channel ids — a union,
 * not an intersection). The only difference for Favorites is that it is
 * matched locally via `channel.isFavorite` instead of a network-resolved id
 * set.
 *
 * @param state Current guide state snapshot (read-only or Draft).
 * @param channel Channel to test membership for.
 * @returns Whether the channel passes the active filter selection.
 */
export const matchesActiveGuideFilters = (
  state: GuideState | Draft<GuideState>,
  channel: TChannel
): boolean => {
  const key: string | undefined = getChannelFilterKey(channel);
  if (key === undefined) {
    return false;
  }
  // Normalized (see normalizeChannelFilterId) so a global channel's
  // "tivo:gc.NNN" channelId still matches the "tivo:ch.NNN" id
  // /v2/guideChannelIds returns for the same channel.
  const normalizedKey: string = normalizeChannelFilterId(key);
  // Favorites needs no network resolution — membership is the channel's own
  // flag, always current.
  if (channel.isFavorite === true && isFavoritesFilterSelected(state)) {
    return true;
  }
  return (
    state.categoryFilterResolutionStatus === "resolved" &&
    state.categoryFilterChannelIds.some(
      (id: string): boolean => normalizeChannelFilterId(id) === normalizedKey
    )
  );
};

/**
 * Ids returned by `/v2/guideChannelIds` that don't correspond to any channel
 * in `channels`, comparing on the same normalized key `matchesActiveGuideFilters`
 * uses (see `normalizeChannelFilterId`). The API is known to occasionally
 * return channel ids that aren't present in our channel list at all — not
 * the "gc."/"ch." prefix mismatch `normalizeChannelFilterId` already works
 * around, but ids with no corresponding channel whatsoever. This is
 * diagnostic-only: the caller logs the result and otherwise ignores it,
 * since those ids simply never match any channel in
 * `matchesActiveGuideFilters` and so are already excluded from the filtered
 * view without any special handling.
 *
 * @param categoryChannelIds Resolved category ids to check.
 * @param channels Full (unfiltered) channel list to check them against.
 * @returns The subset of `categoryChannelIds` with no matching channel.
 */
export const findUnmatchedCategoryChannelIds = (
  categoryChannelIds: readonly string[],
  channels: readonly TChannel[]
): string[] => {
  const knownKeys: Set<string> = new Set(
    channels
      .map(getChannelFilterKey)
      .filter((key: string | undefined): key is string => key !== undefined)
      .map(normalizeChannelFilterId)
  );
  return categoryChannelIds.filter(
    (id: string): boolean => !knownKeys.has(normalizeChannelFilterId(id))
  );
};

/**
 * True when any guide filter (Favorites and/or one or more categories) is
 * currently selected — i.e. the channel list should be narrowed at all.
 * Distinct from `matchesActiveGuideFilters`, which tests a single channel.
 *
 * @param state Current guide state snapshot (read-only or Draft).
 * @returns Whether the effective channel list is currently narrowed.
 */
export const isAnyGuideFilterActive = (
  state: GuideState | Draft<GuideState>
): boolean => state.selectedFilterIds.length > 0;

/**
 * Returns the number of channels that are currently visible under the active
 * filter selection. When no filter is active, returns state.channelCount.
 * When any filter is active, returns the count of channels matching the
 * union of the selected filters (see `matchesActiveGuideFilters`).
 *
 * @param state Current guide state snapshot (read-only or Draft).
 * @returns Effective channel count for display and navigation.
 */
export const getEffectiveChannelCount = (
  state: GuideState | Draft<GuideState>
): number => {
  if (isAnyGuideFilterActive(state)) {
    let count: number = 0;
    for (let i: number = 0; i < state.channels.length; i++) {
      if (matchesActiveGuideFilters(state, state.channels[i])) {
        count++;
      }
    }
    return count;
  }
  return state.channelCount;
};

/**
 * Maps a filtered-list index to the absolute index in state.channels.
 * When no filter is active, the filtered index equals the absolute index.
 * When any filter is active, skips non-matching channels to find the Nth
 * channel matching the union of the selected filters.
 *
 * @param state Current guide state snapshot (read-only or Draft).
 * @param filteredIndex Index within the effective (filtered) channel list.
 * @returns Absolute index in state.channels.
 */
export const getAbsoluteChannelIndex = (
  state: GuideState | Draft<GuideState>,
  filteredIndex: number
): number => {
  if (isAnyGuideFilterActive(state)) {
    let count: number = 0;
    for (let i: number = 0; i < state.channels.length; i++) {
      if (matchesActiveGuideFilters(state, state.channels[i])) {
        if (count === filteredIndex) {
          return i;
        }
        count++;
      }
    }
    return filteredIndex;
  }
  return filteredIndex;
};

/**
 * Applies a channel's `isFavorite` value to `state.channels` (matching by
 * channelId, falling back to stationId when channelId is undefined or does
 * not match any channel).
 *
 * Idempotent — applying the same `(channelId, isFavorite)` pair twice in a
 * row is a no-op, satisfying the optimistic-update idempotency
 * requirement (§6).
 *
 * No-ops silently when neither `channelId` nor `stationId` match any channel
 * (edge case: channel not found during toggle).
 *
 * @param state Mutable guide slice draft.
 * @param channelId Primary match key.
 * @param stationId Fallback match key used when `channelId` does not match.
 * @param isFavorite Value to apply.
 */
export const applyFavoriteChannelUpdateToState = (
  state: Draft<GuideState>,
  channelId: string | undefined,
  stationId: string | undefined,
  isFavorite: boolean
): void => {
  const index: number = state.channels.findIndex(
    (ch: TChannel): boolean =>
      channelId !== undefined && ch.channelId === channelId
  );

  if (index !== -1) {
    state.channels[index].isFavorite = isFavorite;
  } else if (stationId !== undefined) {
    const fallbackIndex: number = state.channels.findIndex(
      (ch: TChannel): boolean => ch.stationId === stationId
    );
    if (fallbackIndex !== -1) {
      state.channels[fallbackIndex].isFavorite = isFavorite;
    }
  }
};

/**
 * Finds the favorite channel with the lowest numeric channel number.
 * Non-numeric channel numbers are ignored. Returns null when there are no
 * favorites with a parseable channel number.
 *
 * @param channels Channel list to search (typically already favorites-only).
 * @returns The lowest-numbered favorite channel, or null.
 */
export const findLowestNumberedFavoriteChannel = (
  channels: TChannel[]
): TChannel | null => {
  let lowest: TChannel | null = null;
  let lowestValue: number = Number.POSITIVE_INFINITY;

  for (const channel of channels) {
    if (channel.isFavorite !== true || channel.channelNumber === undefined) {
      continue;
    }
    const numeric: number = Number.parseFloat(channel.channelNumber);
    if (!Number.isNaN(numeric) && numeric < lowestValue) {
      lowestValue = numeric;
      lowest = channel;
    }
  }

  return lowest;
};

/**
 * Builds the list of visible channel rows directly from guide state.
 *
 * @param state Current guide state snapshot.
 * @returns Visible channel rows with wrap-around offset handling.
 */
export const buildVisibleChannelRows = (
  state: GuideState
): GuideVisibleChannelRow[] => {
  if (isAnyGuideFilterActive(state)) {
    // Filtered mode: collect channels matching the active filter union, with
    // their absolute indices.
    const effectiveChannels: Array<{
      channel: TChannel;
      absoluteIndex: number;
    }> = [];
    for (let i: number = 0; i < state.channels.length; i++) {
      if (matchesActiveGuideFilters(state, state.channels[i])) {
        effectiveChannels.push({
          channel: state.channels[i],
          absoluteIndex: i
        });
      }
    }
    const total: number = effectiveChannels.length;
    if (total === 0) {
      return [];
    }
    const offset: number = state.channelScrollOffset;
    const count: number = Math.min(GUIDE_VISIBLE_ROW_COUNT, total);
    const result: GuideVisibleChannelRow[] = [];
    for (let index: number = 0; index < count; index++) {
      const effectiveIndex: number = (offset + index) % total;
      const { channel, absoluteIndex } = effectiveChannels[effectiveIndex];
      result.push({ channel, rowIndex: absoluteIndex });
    }
    return result;
  }

  // Unfiltered mode: show all channels with circular offset.
  const channels: TChannel[] = state.channels;
  const total: number = channels.length;
  if (total === 0) {
    return [];
  }

  const offset: number = state.channelScrollOffset;
  const count: number = Math.min(GUIDE_VISIBLE_ROW_COUNT, total);
  const result: GuideVisibleChannelRow[] = [];

  for (let index: number = 0; index < count; index++) {
    const channelIndex: number = (offset + index) % total;
    result.push({ channel: channels[channelIndex], rowIndex: channelIndex });
  }

  return result;
};

/**
 * Result of resolving the cached channel window for the current scroll state.
 */
export interface GuideWindowChannels {
  channels: TChannel[];
  startGlobalIndex: number;
}

/**
 * Resolves the contiguous, circular slice of channels that should be held in
 * the in-memory guide cache for the current scroll position.
 *
 * The window spans GUIDE_TOTAL_CACHED_CHANNELS channels: GUIDE_CHANNEL_BUFFER_TOP
 * channels above the first visible row, the visible rows, and the remaining
 * channels below as bottom buffer. Wrap-around is handled circularly so the
 * window always contains exactly the visible rows plus buffers.
 *
 * Computed over the *effective* (filtered) channel list when a guide filter
 * is active — `channelScrollOffset` is always filtered-space-relative in
 * that case (see `getEffectiveChannelCount`/`getAbsoluteChannelIndex`), so
 * indexing `state.channels` directly here would resolve the wrong slice.
 *
 * @param state Current guide state snapshot.
 * @returns The window channels and the global index of the window's first channel.
 */
export const getGuideWindowChannels = (
  state: GuideState
): GuideWindowChannels => {
  const total: number = getEffectiveChannelCount(state);
  if (total === 0) {
    return { channels: [], startGlobalIndex: -1 };
  }

  const size: number = Math.min(
    GUIDE_TOTAL_CACHED_CHANNELS,
    GUIDE_CHANNEL_BUFFER_TOP +
      GUIDE_VISIBLE_ROW_COUNT +
      GUIDE_CHANNEL_BUFFER_BOTTOM,
    total
  );
  const startEffectiveIndex: number =
    (((state.channelScrollOffset - GUIDE_CHANNEL_BUFFER_TOP) % total) + total) %
    total;

  const channels: TChannel[] = [];
  for (let index: number = 0; index < size; index++) {
    const absoluteIndex: number = getAbsoluteChannelIndex(
      state,
      (startEffectiveIndex + index) % total
    );
    channels.push(state.channels[absoluteIndex]);
  }

  return {
    channels,
    startGlobalIndex: getAbsoluteChannelIndex(state, startEffectiveIndex)
  };
};

/** Direction of a vertical (channel-axis) scroll. */
export type GuideChannelScrollDirection = "up" | "down";

/**
 * Plan describing which channels' guide data should be prefetched after a
 * vertical scroll brings new channels toward the edge of the cached window.
 */
export interface GuideChannelPrefetchPlan {
  channels: TChannel[];
  startGlobalIndex: number;
}

/**
 * Resolves the channels whose guide data should be prefetched when the cached
 * channel window scrolls in the given direction. On a downward scroll the
 * bottom GUIDE_PREFETCH_CH_DOWN_COUNT window channels are returned; on an upward
 * scroll the top GUIDE_PREFETCH_CH_UP_COUNT. Returns null when there are no
 * channels. (Spec §3.2.)
 *
 * @param state Current guide state snapshot.
 * @param direction Vertical scroll direction.
 * @returns Channels to prefetch with their start global index, or null.
 */
export const getChannelPrefetchPlan = (
  state: GuideState,
  direction: GuideChannelScrollDirection
): GuideChannelPrefetchPlan | null => {
  const window: GuideWindowChannels = getGuideWindowChannels(state);
  if (window.channels.length === 0) {
    return null;
  }

  const total: number = window.channels.length;
  if (direction === "down") {
    const count: number = Math.min(GUIDE_PREFETCH_CH_DOWN_COUNT, total);
    const offset: number = total - count;
    return {
      channels: window.channels.slice(offset),
      startGlobalIndex: window.startGlobalIndex + offset
    };
  }

  const count: number = Math.min(GUIDE_PREFETCH_CH_UP_COUNT, total);
  return {
    channels: window.channels.slice(0, count),
    startGlobalIndex: window.startGlobalIndex
  };
};

/**
 * Returns whether a horizontal time prefetch should run: true once the cached
 * buffer remaining ahead in the move direction has fallen to at most
 * GUIDE_PREFETCH_TIME_THRESHOLD_SLOTS slots. (Spec §3.1.)
 *
 * @param remainingBufferSlots Buffer slots still cached ahead in the direction.
 * @returns Whether a time prefetch should run.
 */
export const shouldPrefetchTime = (remainingBufferSlots: number): boolean =>
  remainingBufferSlots <= GUIDE_PREFETCH_TIME_THRESHOLD_SLOTS;

/**
 * The time window bounds computed for the initial guide load. (Spec §6.)
 */
export interface InitialTimeWindow {
  visibleStart: DateTime;
  visibleEnd: DateTime;
  fetchStart: DateTime;
  fetchEnd: DateTime;
}

/**
 * Calculates the initial fetch and visible time windows for the guide load.
 * (Spec §6 — calculateInitialTimeWindow. [REQ-GUIDEBATCH-FUN-013])
 *
 * Fixed 2.5-hour composition aligned to 30-minute boundaries:
 *   - 30 min past  (edge container anchor)
 *   - 90 min visible viewport (GUIDE_WINDOW_DURATION_MINUTES)
 *   - 30 min future (next horizontal page primer)
 *
 *   fetchStart = visibleStart − 30 min
 *   fetchEnd   = visibleStart + 120 min
 *
 * @returns The four window boundaries needed for initial population.
 */
export const calculateInitialTimeWindow = (): InitialTimeWindow => {
  const visibleStart: DateTime = new DateTime().floorByMinutes(30);
  const visibleEnd: DateTime = visibleStart.addMinutes(
    GUIDE_WINDOW_DURATION_MINUTES
  );
  const fetchStart: DateTime = visibleStart.addMinutes(-30);
  const fetchEnd: DateTime = visibleStart.addMinutes(120);
  return { visibleStart, visibleEnd, fetchStart, fetchEnd };
};

// ─── Guide Data Guards ─────────────────────────────────────────────────────

type TGuideCell = TGridGuideRow["guideCells"][number];

/**
 * Returns true when the row is null/undefined or contains no guide cells.
 *
 * @param row Source guide row, or null/undefined.
 * @returns True when the row is empty or absent.
 */
const isEmptyGuideRow = (
  row: TGridGuideRow | null | undefined
): row is null | undefined =>
  row === null ||
  row === undefined ||
  row.guideCells === null ||
  row.guideCells === undefined ||
  row.guideCells.length === 0;

/**
 * Builds secondary edge cells without clipping so adjacent-window data remains visible.
 *
 * @param row Source guide row.
 * @param rowIndex Absolute row index.
 * @returns Secondary program cells for the row.
 */
const buildSecondaryProgramCells = (
  row: TGridGuideRow | undefined,
  rowIndex: number
): GuideCellView[] => {
  if (isEmptyGuideRow(row)) {
    return [];
  }

  const result: GuideCellView[] = [];

  for (
    let cellIndex: number = 0;
    cellIndex < row.guideCells.length;
    cellIndex++
  ) {
    const cell = row.guideCells[cellIndex];
    result.push({
      props: cell,
      uiProps: {
        id:
          (cell.contentId || "") +
          "-secondary-" +
          rowIndex +
          "-" +
          cellIndex +
          "-" +
          cell.startTime.getTime(),
        width: cell.duration / SECONDS_IN_A_MINUTE,
        isFocused: false,
        isDimmed: !(cell.isStartover === true || cell.isCatchup === true)
      }
    });
  }

  return result;
};

/**
 * Lazily-initialized sentinel used as the props of every placeholder GuideCellView.
 *
 * Deferred to first use so that new DateTime(0) is never called at module
 * evaluation time — which would fail in test environments where the DateTime
 * constructor is not yet bound when the module is first required.
 *
 * Once created the frozen object is cached and returned on every subsequent
 * call, so the reference is stable across all renders. useSelector's
 * reference-equality check therefore bails out whenever focus is on (or passes
 * through) a placeholder, preventing the forceStoreRerender → setState →
 * re-render loop that occurs during fast horizontal navigation.
 *
 * Callers must guard on uiProps.isPlaceholder before reading any field.
 */
let mPlaceholderGuideCell: TGuideCell | null = null;

const getPlaceholderGuideCell = (): TGuideCell => {
  if (mPlaceholderGuideCell === null) {
    mPlaceholderGuideCell = Object.freeze({
      collectionId: "",
      contentId: "",
      duration: 0,
      offerId: "",
      posterImageUrl: "",
      startTime: new DateTime(0)
    });
  }
  return mPlaceholderGuideCell;
};

/**
 * Creates a GuideCellView placeholder with the given UI width.
 * Used by view components to fill empty/missing program slots.
 *
 * @param id Unique id for the placeholder cell.
 * @param width Width in minutes.
 * @returns A GuideCellView placeholder with uiProps.isPlaceholder set to true.
 */
export const createGuideCellViewPlaceholder = (
  id: string,
  width: number
): GuideCellView => ({
  props: getPlaceholderGuideCell(),
  uiProps: {
    id,
    width,
    isFocused: false,
    isDimmed: true,
    isPlaceholder: true
  }
});

/** Blank channel — renders GuideChannelCell with no logo/callsign/number. */
const EMPTY_GUIDE_CHANNEL: TChannel = {
  channelId: "",
  channelNumber: "",
  channelLogoUrl: "",
  callSign: "",
  isFavorite: false,
  isSubscribed: false
} as TChannel;

/**
 * Fills the row area with empty channel/program cells before channels and
 * programs have loaded, so the grid's shape is present on first paint and
 * rows fill in place as their data arrives instead of popping in.
 * Used by GuideScreen as a fallback while selectGuideVisibleRows is empty.
 */
export const EMPTY_GUIDE_ROWS: GuideVisibleRowView[] = Array.from(
  { length: GUIDE_VISIBLE_ROW_COUNT },
  (_, index: number): GuideVisibleRowView => ({
    channel: EMPTY_GUIDE_CHANNEL,
    rowIndex: index,
    isChannelFocused: false,
    hasSocu: false,
    primaryPrograms: [],
    secondaryPrograms: []
  })
);

/**
 * Creates a focusable GuideCellView fallback cell shown in place of program
 * data when the guide-rows (EPG program schedule) service has failed to load
 * (see GuideState.isGuideRowsFailed). Unlike createGuideCellViewPlaceholder,
 * this cell is focusable and tunable — selecting it starts live playback for
 * the row's channel.
 *
 * @param id Unique id for the fallback cell.
 * @param width Width in minutes.
 * @returns A GuideCellView with uiProps.isTuneFallback set to true.
 */
export const createGuideCellViewTuneFallback = (
  id: string,
  width: number
): GuideCellView => ({
  props: getPlaceholderGuideCell(),
  uiProps: {
    id,
    width,
    isFocused: false,
    isDimmed: false,
    isTuneFallback: true
  }
});

/**
 * A rendered guide cell together with its window-clipped time span. Both the
 * view layer and navigation share these so that focus, placeholders and real
 * programs all live in one gapless, left-to-right coordinate system.
 */
interface BuiltCell {
  view: GuideCellView;
  startMs: number;
  endMs: number;
}

/** A real program cell collected from the buckets, with its absolute span. */
interface MergedProgramCell {
  id: string;
  cell: TGuideCell;
  startMs: number;
  endMs: number;
}

/**
 * Collects the real program cells overlapping the window from the past/current/
 * future buckets, de-duplicated and sorted left-to-right.
 *
 * @param bucketRows Past, current and future rows for the channel.
 * @param rowIndex Absolute row index (used only for stable cell ids).
 * @param windowStartMs Visible window start in ms.
 * @param windowEndMs Visible window end in ms.
 * @returns Sorted, de-duplicated program cells overlapping the window.
 */
const collectWindowMergedCells = (
  bucketRows: Array<TGridGuideRow | undefined>,
  rowIndex: number,
  windowStartMs: number,
  windowEndMs: number
): MergedProgramCell[] => {
  const mergedCells: MergedProgramCell[] = [];
  const dedupeKeys: Set<string> = new Set();

  for (
    let bucketIndex: number = 0;
    bucketIndex < bucketRows.length;
    bucketIndex++
  ) {
    const row: TGridGuideRow | undefined = bucketRows[bucketIndex];
    if (isEmptyGuideRow(row)) {
      continue;
    }

    for (
      let cellIndex: number = 0;
      cellIndex < row.guideCells.length;
      cellIndex++
    ) {
      const cell = row.guideCells[cellIndex];
      if (cell.startTime === null || cell.startTime === undefined) {
        continue;
      }
      const startMs: number = cell.startTime.getTime();
      const endMs: number = startMs + cell.duration * MILLISECONDS_IN_A_SECOND;

      if (startMs >= windowEndMs || endMs <= windowStartMs) {
        continue;
      }

      const dedupeKey: string =
        startMs + "|" + endMs + "|" + (cell.title || "");
      if (dedupeKeys.has(dedupeKey)) {
        continue;
      }
      dedupeKeys.add(dedupeKey);

      mergedCells.push({
        id:
          (cell.contentId || "") +
          "-" +
          startMs +
          "-" +
          rowIndex +
          "-" +
          bucketIndex +
          "-" +
          cellIndex,
        cell,
        startMs,
        endMs
      });
    }
  }

  mergedCells.sort((a, b): number => {
    if (a.startMs === b.startMs) {
      return a.endMs - b.endMs;
    }
    return a.startMs - b.startMs;
  });

  return mergedCells;
};

/**
 * Builds the gapless rendered cells for a row by merging past/current/future
 * buckets and filling any uncovered span with a loading placeholder. Every
 * returned cell (real or placeholder) carries its window-clipped time span so
 * navigation can move over placeholders and anchor focus by air-time.
 *
 * @param state Current guide state snapshot.
 * @param rowIndex Absolute row index.
 * @param guideStartTime Clip start time for the visible guide window.
 * @returns Render-ready cells with time spans for the row.
 */
const buildRowCells = (
  state: GuideState,
  rowIndex: number,
  guideStartTime: DateTime
): BuiltCell[] => {
  const currentWindowEndTime: DateTime = guideStartTime.addMinutes(
    GUIDE_WINDOW_DURATION_MINUTES
  );
  const windowStartMs: number = guideStartTime.getTime();
  const windowEndMs: number = currentWindowEndTime.getTime();
  const msPerMinute: number = SECONDS_IN_A_MINUTE * MILLISECONDS_IN_A_SECOND;

  // Guide-rows service failure: no program schedule is available. Render a
  // focusable "Select to tune to channel" cell spanning all but the last
  // time slot, followed by a dimmed, non-focusable placeholder filling the
  // trailing slot — instead of attempting to build program cells from empty
  // bucket rows.
  if (state.isGuideRowsFailed) {
    const fallbackEndMs: number =
      windowStartMs +
      (GUIDE_WINDOW_DURATION_MINUTES - GUIDE_TIME_SLOT_INTERVAL_MINUTES) *
        msPerMinute;
    return [
      {
        view: createGuideCellViewTuneFallback(
          "tune-fallback-" + rowIndex,
          GUIDE_WINDOW_DURATION_MINUTES - GUIDE_TIME_SLOT_INTERVAL_MINUTES
        ),
        startMs: windowStartMs,
        endMs: fallbackEndMs
      },
      {
        view: createGuideCellViewPlaceholder(
          "tune-fallback-trailing-" + rowIndex,
          GUIDE_TIME_SLOT_INTERVAL_MINUTES
        ),
        startMs: fallbackEndMs,
        endMs: windowEndMs
      }
    ];
  }

  const bucketRows: Array<TGridGuideRow | undefined> = [
    state.guideRows.pastRows[rowIndex],
    state.guideRows.currentRows[rowIndex],
    state.guideRows.futureRows[rowIndex]
  ];

  const mergedCells: MergedProgramCell[] = collectWindowMergedCells(
    bucketRows,
    rowIndex,
    windowStartMs,
    windowEndMs
  );

  const result: BuiltCell[] = [];

  // Walk the visible window left→right. Any span not covered by a real program
  // cell is filled with a loading placeholder so the row always spans the full
  // window width without gaps while data is still being fetched.
  let cursorMs: number = windowStartMs;

  // When the visible window's loading has timed out, still-loading placeholders
  // render as navigable, non-selectable "TBA" cells.
  const loadingTimedOut: boolean = state.guideLoadingTimedOut === true;

  const pushPlaceholder = (spanStartMs: number, spanEndMs: number): void => {
    result.push({
      view: {
        props: getPlaceholderGuideCell(),
        uiProps: {
          id: "placeholder-" + rowIndex + "-" + spanStartMs,
          width: (spanEndMs - spanStartMs) / msPerMinute,
          isFocused: false,
          isDimmed: true,
          isPlaceholder: true,
          isTba: loadingTimedOut
        }
      },
      startMs: spanStartMs,
      endMs: spanEndMs
    });
  };

  for (let index: number = 0; index < mergedCells.length; index++) {
    const merged = mergedCells[index];
    const visibleStartMs: number = Math.max(merged.startMs, windowStartMs);
    const visibleEndMs: number = Math.min(merged.endMs, windowEndMs);

    // Skip cells fully behind the cursor (already covered by an earlier cell).
    if (visibleEndMs <= cursorMs) {
      continue;
    }

    // Fill the gap before this cell with a loading placeholder.
    if (visibleStartMs > cursorMs) {
      pushPlaceholder(cursorMs, visibleStartMs);
    }

    const effectiveStartMs: number = Math.max(visibleStartMs, cursorMs);
    result.push({
      view: {
        props: merged.cell,
        uiProps: {
          id: merged.id,
          width: (visibleEndMs - effectiveStartMs) / msPerMinute,
          isFocused: false,
          isDimmed: !(
            merged.cell.isStartover === true || merged.cell.isCatchup === true
          ),
          isPlaceholder: false
        }
      },
      startMs: effectiveStartMs,
      endMs: visibleEndMs
    });
    cursorMs = visibleEndMs;
  }

  // Fill any remaining span after the last cell (or the whole window when the
  // row has no data yet) with a loading placeholder.
  if (cursorMs < windowEndMs) {
    pushPlaceholder(cursorMs, windowEndMs);
  }

  return result;
};

/**
 * Resolves the gapless rendered cells for the on-screen row at
 * `visibleRowIndex`. Used by the navigation helpers so focus, stalls and the
 * time anchor all operate over exactly what the user sees (real programs and
 * loading placeholders alike).
 *
 * When no time window is established yet (before the guide has mounted its
 * window), falls back to the raw current-row cells so navigation still resolves
 * against real programs. In production a window is always present, so the
 * rendered-cell path drives behaviour.
 *
 * @param state Guide state (read-only or reducer draft).
 * @param visibleRowIndex Visible row index on screen (0-based).
 * @returns Rendered cells with time spans, or an empty array when unavailable.
 */
const getVisibleRowCells = (
  state: GuideState | Draft<GuideState>,
  visibleRowIndex: number
): BuiltCell[] => {
  const total: number = getEffectiveChannelCount(state);
  if (total === 0) {
    return [];
  }
  const effectiveIndex: number =
    (state.channelScrollOffset + visibleRowIndex) % total;
  const absoluteIndex: number = getAbsoluteChannelIndex(state, effectiveIndex);

  const guideStartTime: DateTime | null = getGuideStartTime(
    state as GuideState
  );
  if (guideStartTime !== null) {
    return buildRowCells(state as GuideState, absoluteIndex, guideStartTime);
  }

  // No window yet — resolve against the raw current-row cells.
  const row = state.guideRows.currentRows[absoluteIndex];
  if (isEmptyGuideRow(row as TGridGuideRow | null | undefined)) {
    return [];
  }
  const fallbackCells: BuiltCell[] = [];
  for (let index: number = 0; index < row.guideCells.length; index++) {
    const cell = row.guideCells[index];
    const hasStartTime: boolean =
      cell.startTime !== null && cell.startTime !== undefined;
    const startMs: number = hasStartTime ? cell.startTime.getTime() : 0;
    fallbackCells.push({
      view: createGuideCellViewPlaceholder(
        "raw-" + absoluteIndex + "-" + index,
        0
      ),
      startMs,
      endMs: hasStartTime
        ? startMs + cell.duration * MILLISECONDS_IN_A_SECOND
        : 0
    });
  }
  return fallbackCells;
};

/**
 * Returns true when the channel has a SOCU (start-over / catch-up) policy
 * with a non-zero catch-up availability window.
 * Used to pre-compute the `hasSocu` display flag on each visible row so
 * views never repeat this policyInfo traversal.
 *
 * @param channel Channel to evaluate.
 * @returns True when cuAvailabilityDurationSeconds > 0 for any SOCU policy entry.
 */
const isChannelSocuCapable = (channel: TChannel): boolean => {
  const policyInfo = channel.policyInfo;
  if (policyInfo === null || policyInfo === undefined) {
    return false;
  }
  for (let i: number = 0; i < policyInfo.length; i++) {
    const socuRules = policyInfo[i].socuRules;
    if (
      socuRules !== null &&
      socuRules !== undefined &&
      (socuRules.cuAvailabilityDurationSeconds || 0) > 0
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Builds the visible rows for the current guide window.
 *
 * @param state Current guide state snapshot.
 * @returns Render-ready row data for GuideScreen.
 */
export const buildVisibleGuideRows = (
  state: GuideState
): GuideVisibleRowView[] => {
  const guideStartTime: DateTime | null = getGuideStartTime(state);
  if (guideStartTime === null) {
    return [];
  }

  const visibleChannels: GuideVisibleChannelRow[] =
    buildVisibleChannelRows(state);
  const effectiveVerticalPosition: number = Math.min(
    state.verticalPosition,
    GUIDE_HIGHLIGHT_ROW_MAX
  );
  const effectiveHorizontalPosition: number = Math.max(
    state.horizontalPosition,
    0
  );
  const result: GuideVisibleRowView[] = [];

  for (
    let visibleIndex: number = 0;
    visibleIndex < visibleChannels.length;
    visibleIndex++
  ) {
    const visibleChannel: GuideVisibleChannelRow =
      visibleChannels[visibleIndex];
    const rowIndex: number = visibleChannel.rowIndex;
    const builtCells: BuiltCell[] = buildRowCells(
      state,
      rowIndex,
      guideStartTime
    );

    const isJumpChannelRow: boolean =
      visibleChannel.channel.jumpChannelInfo !== undefined;

    // Loading placeholders are fully navigable: the 1-based horizontalPosition
    // selects the Nth rendered cell in the focused row (real program or loading
    // placeholder), so focus never stalls on missing data. When data arrives,
    // mergeGuideRows re-anchors horizontalPosition by air-time so focus stays
    // put rather than jumping as placeholders become real programs.
    // No cell is highlighted while the filter sidebar owns focus.
    const focusedCellIndex: number =
      !state.isSidebarFocused &&
      effectiveVerticalPosition === visibleIndex &&
      effectiveHorizontalPosition >= 1
        ? Math.min(effectiveHorizontalPosition, builtCells.length) - 1
        : -1;

    const primaryPrograms: GuideCellView[] = [];
    for (
      let programIndex: number = 0;
      programIndex < builtCells.length;
      programIndex++
    ) {
      const cellView: GuideCellView = builtCells[programIndex].view;
      const isPlaceholderCell: boolean =
        cellView.uiProps.isPlaceholder === true;
      primaryPrograms.push({
        ...cellView,
        uiProps: {
          ...cellView.uiProps,
          isDimmed:
            isJumpChannelRow && !isPlaceholderCell
              ? false
              : cellView.uiProps.isDimmed,
          isFocused: programIndex === focusedCellIndex
        }
      });
    }

    const secondaryPrograms: GuideCellView[] = buildSecondaryProgramCells(
      state.isPastGuide
        ? state.guideRows.futureRows[rowIndex]
        : state.guideRows.pastRows[rowIndex],
      rowIndex
    );

    result.push({
      channel: visibleChannel.channel,
      rowIndex,
      isChannelFocused:
        effectiveVerticalPosition === visibleIndex &&
        effectiveHorizontalPosition === 0 &&
        !state.isSidebarFocused,
      hasSocu: isChannelSocuCapable(visibleChannel.channel),
      primaryPrograms,
      secondaryPrograms
    });
  }

  return result;
};

/**
 * Builds the time-slot labels for the guide header.
 *
 * @param state Current guide state snapshot.
 * @returns Time-slot labels for the active guide window.
 */
export const buildTimeSlotLabels = (
  state: GuideState
): Array<{ label: string }> => {
  const guideStartTime: DateTime | null = getGuideStartTime(state);
  if (guideStartTime === null) {
    return [];
  }

  const slotCount: number =
    GUIDE_WINDOW_DURATION_MINUTES / GUIDE_TIME_SLOT_INTERVAL_MINUTES;
  const slotSpanMs: number =
    GUIDE_TIME_SLOT_INTERVAL_MINUTES *
    SECONDS_IN_A_MINUTE *
    MILLISECONDS_IN_A_SECOND;
  const labels: string[] = guideStartTime.generateIntervals(
    slotSpanMs,
    slotCount * slotSpanMs
  );

  const result: Array<{ label: string }> = [];
  for (let index: number = 0; index < labels.length; index++) {
    result.push({ label: labels[index] });
  }

  return result;
};

// ─── Position Helpers ─────────────────────────────────────────────────────────

/**
 * Returns the clamped vertical position within the lock zone.
 * The highlight can never exceed GUIDE_HIGHLIGHT_ROW_MAX.
 *
 * @param state Mutable guide slice draft or read-only state.
 * @returns Clamped vertical position.
 */
export const getEffectiveVerticalPosition = (
  state: Draft<GuideState>
): number => Math.min(state.verticalPosition, GUIDE_HIGHLIGHT_ROW_MAX);

/**
 * Computes the channelScrollOffset that places the channel matching
 * `channelNumber` under the highlight row.
 *
 * The highlighted absolute channel index is
 * `(channelScrollOffset + effectiveVerticalPosition) % channelCount`, so the
 * offset is derived by inverting that relationship (with wrap-around).
 *
 * @param channels Ordered guide channel list.
 * @param channelNumber Channel number to position the highlight on.
 * @param effectiveVerticalPosition Highlight row index within the visible grid.
 * @returns The channelScrollOffset, or null when the channel is not found.
 */
export const computeChannelScrollOffsetForChannel = (
  channels: TChannel[],
  channelNumber: string,
  effectiveVerticalPosition: number
): number | null => {
  const total: number = channels.length;
  if (total === 0) {
    return null;
  }

  const channelIndex: number = channels.findIndex(
    (channel: TChannel): boolean => channel.channelNumber === channelNumber
  );

  if (channelIndex === -1) {
    return null;
  }

  return (((channelIndex - effectiveVerticalPosition) % total) + total) % total;
};

/**
 * Returns the max horizontal position for the visible row at `visibleRowIndex`.
 * Uses channelScrollOffset to resolve the visible index to the absolute
 * channel index, then counts guide cells in that row.
 *
 * @param state Mutable guide slice draft.
 * @param visibleRowIndex Visible row index on screen (0-based).
 * @returns Max horizontal position (= number of guide cells), or 0 if unknown.
 */
export const getMaxHorizontalPosition = (
  state: Draft<GuideState>,
  visibleRowIndex: number
): number => {
  const total: number = getEffectiveChannelCount(state);
  if (total === 0) {
    return 0;
  }

  // Guide-rows service failure: every row renders exactly one focusable
  // "Select to tune to channel" fallback cell, regardless of cached data.
  if (state.isGuideRowsFailed) {
    return 1;
  }

  // The row always renders a gapless run of cells across the window (real
  // programs plus loading placeholders for uncovered spans), so the max focus
  // position is simply the number of rendered cells.
  return getVisibleRowCells(state, visibleRowIndex).length;
};

/**
 * Clamps the horizontal focus position within the focused row bounds.
 *
 * @param state Mutable guide slice draft.
 * @param visibleRowIndex Visible row index on screen (0-based).
 * @returns Effective horizontal focus position, clamped to [0, max].
 */
const getEffectiveHorizontalPosition = (
  state: Draft<GuideState>,
  visibleRowIndex: number
): number => {
  const maxHorizontalPosition: number = getMaxHorizontalPosition(
    state,
    visibleRowIndex
  );

  if (state.horizontalPosition < 0) {
    return 0;
  }

  return Math.min(state.horizontalPosition, maxHorizontalPosition);
};

// ─── Time-Anchor Helpers ──────────────────────────────────────────────────────

/**
 * Returns the start time (ms) of the program cell at the given focus position,
 * or 0 when the position refers to the channel cell or the row has no data.
 *
 * @param state Mutable guide slice draft.
 * @param visibleRowIndex Visible row index on screen (0-based).
 * @param horizontalPosition Horizontal position (0 = channel cell, 1..N = program index).
 * @returns Start time in milliseconds, or 0 if unavailable.
 */
export const getFocusedCellStartMs = (
  state: Draft<GuideState>,
  visibleRowIndex: number,
  horizontalPosition: number
): number => {
  if (horizontalPosition <= 0) {
    return 0;
  }
  const cells: BuiltCell[] = getVisibleRowCells(state, visibleRowIndex);
  const cellIndex: number = horizontalPosition - 1;
  if (cellIndex < 0 || cellIndex >= cells.length) {
    return 0;
  }
  return cells[cellIndex].startMs;
};

/**
 * Finds the horizontal position (1-based program index) for the program cell
 * that is airing at `anchorMs` in the given row.
 *
 * Resolution rules:
 * - If anchorMs falls within a program (start <= anchorMs < end), returns that cell's index + 1.
 * - If anchorMs precedes the first cell, returns 1 (clamp to first).
 * - If anchorMs follows the last cell, returns the last cell's index + 1 (clamp to last).
 * - If the row has no cells, returns 0 (channel cell fallback).
 *
 * @param state Mutable guide slice draft.
 * @param visibleRowIndex Visible row index on screen (0-based).
 * @param anchorMs Anchor timestamp in milliseconds.
 * @returns Horizontal position (1..N), or 0 if row is empty.
 */
export const findHorizontalPositionForTime = (
  state: Draft<GuideState>,
  visibleRowIndex: number,
  anchorMs: number
): number => {
  const cells: BuiltCell[] = getVisibleRowCells(state, visibleRowIndex);
  if (cells.length === 0) {
    return 0;
  }

  for (let index: number = 0; index < cells.length; index++) {
    if (anchorMs >= cells[index].startMs && anchorMs < cells[index].endMs) {
      return index + 1;
    }
  }

  // Before the first rendered cell — clamp to first.
  if (anchorMs < cells[0].startMs) {
    return 1;
  }

  // At or after the last rendered cell — clamp to last.
  return cells.length;
};

// ─── Channel Scroll Primitives ────────────────────────────────────────────────
//
// These primitives power the current single-step D-pad navigation and are
// parameterized by `steps` so future modes (for example, fast scroll or
// page-up/down) can reuse the same behavior without changing core logic.

/**
 * Scrolls the channel list upward (toward earlier channels) by `steps` rows.
 *
 * Current use: D-pad up boundary handling calls this with steps=1.
 * Future use: fast-scroll modes can call with steps>1.
 *
 * @param state Mutable guide slice draft.
 * @param steps Number of rows to scroll. Defaults to 1.
 */
const scrollChannelsUp = (
  state: Draft<GuideState>,
  steps: number = 1
): void => {
  const total: number = getEffectiveChannelCount(state);
  if (total > 0) {
    state.channelScrollOffset =
      (((state.channelScrollOffset - steps) % total) + total) % total;
  }
};

/**
 * Scrolls the channel list downward (toward later channels) by `steps` rows.
 *
 * Current use: D-pad down boundary handling calls this with steps=1.
 * Future use: fast-scroll modes can call with steps>1.
 *
 * @param state Mutable guide slice draft.
 * @param steps Number of rows to scroll. Defaults to 1.
 */
const scrollChannelsDown = (
  state: Draft<GuideState>,
  steps: number = 1
): void => {
  const total: number = getEffectiveChannelCount(state);
  if (total > 0) {
    state.channelScrollOffset = (state.channelScrollOffset + steps) % total;
  }
};

// ─── Vertical Navigation Primitives ──────────────────────────────────────────

/**
 * Moves focus up by one row.
 * Within the lock zone (verticalPosition > GUIDE_HIGHLIGHT_ROW_MIN): decrements
 * verticalPosition.
 * At the top boundary: scrolls the channel list up by one row (circular).
 *
 * Time-anchor behavior: on the first vertical press, lazily seeds the anchor
 * from the currently focused program cell. After moving, resolves the new row's
 * horizontal position from the persisted anchor time so the highlight stays on
 * the same time slot across rows.
 *
 * @param state Mutable guide slice draft.
 */
export const applyNavigateUp = (state: Draft<GuideState>): void => {
  const currentVPos: number = getEffectiveVerticalPosition(state);

  // Lazy-init anchor from currently focused cell
  if (state.highlightAnchorTimeMs === 0 && state.horizontalPosition > 0) {
    const anchorMs: number = getFocusedCellStartMs(
      state,
      currentVPos,
      state.horizontalPosition
    );
    if (anchorMs > 0) {
      state.highlightAnchorTimeMs = anchorMs;
    }
  }

  if (state.verticalPosition > GUIDE_HIGHLIGHT_ROW_MIN) {
    state.verticalPosition -= 1;
  } else {
    scrollChannelsUp(state);
  }

  // Resolve horizontal position from anchor in the new row
  if (state.highlightAnchorTimeMs > 0 && state.horizontalPosition > 0) {
    const newVPos: number = getEffectiveVerticalPosition(state);
    const resolved: number = findHorizontalPositionForTime(
      state,
      newVPos,
      state.highlightAnchorTimeMs
    );
    if (resolved > 0) {
      state.horizontalPosition = resolved;
    }
  }
};

/**
 * Moves focus down by one row.
 * Within the lock zone (verticalPosition < GUIDE_HIGHLIGHT_ROW_MAX): increments
 * verticalPosition.
 * At the bottom boundary: scrolls the channel list down by one row (circular).
 *
 * Time-anchor behavior: on the first vertical press, lazily seeds the anchor
 * from the currently focused program cell. After moving, resolves the new row's
 * horizontal position from the persisted anchor time so the highlight stays on
 * the same time slot across rows.
 *
 * @param state Mutable guide slice draft.
 */
export const applyNavigateDown = (state: Draft<GuideState>): void => {
  const currentVPos: number = getEffectiveVerticalPosition(state);

  // Lazy-init anchor from currently focused cell
  if (state.highlightAnchorTimeMs === 0 && state.horizontalPosition > 0) {
    const anchorMs: number = getFocusedCellStartMs(
      state,
      currentVPos,
      state.horizontalPosition
    );
    if (anchorMs > 0) {
      state.highlightAnchorTimeMs = anchorMs;
    }
  }

  // Bottom boundary is the smaller of the lock-zone max and the last available
  // channel row. When the filtered list is shorter than the visible grid (e.g.
  // a short Favorites view), this keeps the highlight from moving onto an empty
  // row — mirroring the top-boundary scroll behavior.
  const maxVerticalPosition: number = Math.min(
    GUIDE_HIGHLIGHT_ROW_MAX,
    getEffectiveChannelCount(state) - 1
  );
  if (state.verticalPosition < maxVerticalPosition) {
    state.verticalPosition += 1;
  } else {
    scrollChannelsDown(state);
  }

  // Resolve horizontal position from anchor in the new row
  if (state.highlightAnchorTimeMs > 0 && state.horizontalPosition > 0) {
    const newVPos: number = getEffectiveVerticalPosition(state);
    const resolved: number = findHorizontalPositionForTime(
      state,
      newVPos,
      state.highlightAnchorTimeMs
    );
    if (resolved > 0) {
      state.horizontalPosition = resolved;
    }
  }
};

// ─── Horizontal Navigation Primitives ────────────────────────────────────────

/**
 * Moves horizontal focus one step to the left.
 * When hPos > 0: decrements toward the channel cell.
 * When hPos === 0: no-ops — the navigateGuideLeft thunk detects the stall
 * and dispatches shiftTimeAxisBackward.
 *
 * This is the current reducer behavior behind _navigateGuideLeftStep.
 *
 * @param state Mutable guide slice draft.
 */
export const applyHorizontalLeftStep = (state: Draft<GuideState>): void => {
  const effectiveVerticalPosition: number = getEffectiveVerticalPosition(state);
  const effectiveHorizontalPosition: number = getEffectiveHorizontalPosition(
    state,
    effectiveVerticalPosition
  );

  if (effectiveHorizontalPosition > 0) {
    state.horizontalPosition = effectiveHorizontalPosition - 1;

    // Update time anchor from the newly focused cell (skip channel cell at 0)
    if (state.horizontalPosition > 0) {
      const anchorMs: number = getFocusedCellStartMs(
        state,
        effectiveVerticalPosition,
        state.horizontalPosition
      );
      if (anchorMs > 0) {
        state.highlightAnchorTimeMs = anchorMs;
      }
    }
  }
  // hPos === 0: stall — the navigateGuideLeft thunk handles the time shift.
};

/**
 * Moves horizontal focus one step to the right.
 * When hPos < max: increments toward the last program cell.
 * When at max: no-ops — the navigateGuideRight thunk detects the stall
 * and dispatches shiftTimeAxisForward.
 *
 * This is the current reducer behavior behind _navigateGuideRightStep.
 *
 * @param state Mutable guide slice draft.
 */
export const applyHorizontalRightStep = (state: Draft<GuideState>): void => {
  const effectiveVerticalPosition: number = getEffectiveVerticalPosition(state);
  const effectiveHorizontalPosition: number = getEffectiveHorizontalPosition(
    state,
    effectiveVerticalPosition
  );
  const maxHorizontalPosition: number = getMaxHorizontalPosition(
    state,
    effectiveVerticalPosition
  );

  if (effectiveHorizontalPosition < maxHorizontalPosition) {
    state.horizontalPosition = effectiveHorizontalPosition + 1;

    // Update time anchor from the newly focused cell
    const anchorMs: number = getFocusedCellStartMs(
      state,
      effectiveVerticalPosition,
      state.horizontalPosition
    );
    if (anchorMs > 0) {
      state.highlightAnchorTimeMs = anchorMs;
    }
  }
  // hPos === max: stall — the navigateGuideRight thunk handles the time shift.
};

// ─── Time-Axis Shift Helpers ──────────────────────────────────────────────────

/**
 * Converts minutes to milliseconds.
 *
 * @param minutes Duration in minutes.
 * @returns Equivalent duration in milliseconds.
 */
const minutesToMilliseconds = (minutes: number): number =>
  minutes * SECONDS_IN_A_MINUTE * MILLISECONDS_IN_A_SECOND;

/**
 * Computes the 90-minute bucket index for a given timestamp.
 *
 * @param timeMs Absolute timestamp in milliseconds.
 * @param guideSessionStartMs Session start anchor timestamp.
 * @param windowDurationMs Window duration in milliseconds.
 * @returns Signed bucket index relative to session start.
 */
const getBucketIndex = (
  timeMs: number,
  guideSessionStartMs: number,
  windowDurationMs: number
): number => Math.floor((timeMs - guideSessionStartMs) / windowDurationMs);

/**
 * Computes the maximum allowed future timestamp for guide navigation.
 *
 * @param guideSessionStartMs Session start anchor timestamp.
 * @returns Future navigation limit in milliseconds.
 */
const getFutureLimitMs = (guideSessionStartMs: number): number =>
  guideSessionStartMs +
  minutesToMilliseconds(GUIDE_MAX_FUTURE_DAYS * MINUTES_PER_DAY);

/**
 * Computes the maximum allowed past timestamp for guide navigation.
 *
 * @param guideSessionStartMs Session start anchor timestamp.
 * @param epgLookBackHours Maximum hours backward navigation is allowed.
 * @returns Past navigation limit in milliseconds.
 */
const getPastLimitMs = (
  guideSessionStartMs: number,
  epgLookBackHours: number
): number => guideSessionStartMs - minutesToMilliseconds(epgLookBackHours * 60);

/**
 * Rotates guide rows one window forward.
 *
 * @param state Mutable guide slice draft.
 * @returns Nothing.
 */
const rotateBucketsForward = (state: Draft<GuideState>): void => {
  const snap = current(state.guideRows);
  state.guideRows.pastRows = snap.currentRows;
  state.guideRows.currentRows =
    snap.futureRows.length > 0 ? snap.futureRows : snap.currentRows;
  state.guideRows.futureRows = [];
};

/**
 * Rotates guide rows one window backward.
 *
 * @param state Mutable guide slice draft.
 * @returns Nothing.
 */
const rotateBucketsBackward = (state: Draft<GuideState>): void => {
  const snap = current(state.guideRows);
  state.guideRows.futureRows = snap.currentRows;
  state.guideRows.currentRows =
    snap.pastRows.length > 0 ? snap.pastRows : snap.currentRows;
  state.guideRows.pastRows = [];
};

/**
 * Re-derives the past/future anchor windows from the current window so the
 * three windows always tile a contiguous span:
 * [current − 90m, current) · [current, current + 90m) · [current + 90m, +180m).
 *
 * The anchors must be recomputed on *every* time shift, not only when a
 * 90-minute bucket boundary is crossed. A shift moves the current window by
 * GUIDE_SHIFT_MINUTES (30), so advancing the anchors only on bucket rotation
 * left them pinned for the two intermediate steps, opening a 30–60 minute hole
 * between the past and current windows. Programs starting inside that hole are
 * held in the cache but queried by no window, so they never reach any bucket
 * and render as a permanent gap.
 *
 * @param state Mutable guide slice draft.
 * @returns Nothing.
 */
const syncWindowAnchorsToCurrent = (state: Draft<GuideState>): void => {
  if (state.currentWindowStartTime === null) {
    return;
  }

  state.pastWindowStartTime = state.currentWindowStartTime.subtractMinutes(
    GUIDE_WINDOW_DURATION_MINUTES
  );
  state.futureWindowStartTime = state.currentWindowStartTime.addMinutes(
    GUIDE_WINDOW_DURATION_MINUTES
  );
};

/**
 * Promotes rows forward (future→current, current→past) and advances the
 * currentWindowStartTime by GUIDE_SHIFT_MINUTES.
 * Returns true if the shift was applied; false if the future boundary was hit.
 *
 * @param state Mutable guide slice draft.
 * @returns Whether the shift was applied.
 */
export const applyShiftForward = (state: Draft<GuideState>): boolean => {
  if (
    state.currentWindowStartTime === null ||
    state.guideSessionStartMs === 0
  ) {
    return false;
  }
  const currentTimeMs: number = state.currentWindowStartTime.getTime();
  const windowDurationMs: number = minutesToMilliseconds(
    GUIDE_WINDOW_DURATION_MINUTES
  );
  const shiftDurationMs: number = minutesToMilliseconds(GUIDE_SHIFT_MINUTES);
  const futureLimitMs: number = getFutureLimitMs(state.guideSessionStartMs);
  const nextTimeMs: number = currentTimeMs + shiftDurationMs;

  if (nextTimeMs > futureLimitMs) {
    return false;
  }

  state.currentWindowStartTime =
    state.currentWindowStartTime.addMinutes(GUIDE_SHIFT_MINUTES);
  state.horizontalPosition = 1;

  const previousBucketIndex: number = getBucketIndex(
    currentTimeMs,
    state.guideSessionStartMs,
    windowDurationMs
  );
  const nextBucketIndex: number = getBucketIndex(
    nextTimeMs,
    state.guideSessionStartMs,
    windowDurationMs
  );

  if (nextBucketIndex > previousBucketIndex) {
    rotateBucketsForward(state);
  }

  // Every shift, not just a bucket rotation — see syncWindowAnchorsToCurrent.
  syncWindowAnchorsToCurrent(state);

  if (state.currentWindowStartTime.getTime() >= state.guideSessionStartMs) {
    state.isPastGuide = false;
  }

  // Refresh time anchor from the new focused cell after shift
  const effectiveVPosAfterShift: number = getEffectiveVerticalPosition(state);
  const anchorAfterShift: number = getFocusedCellStartMs(
    state,
    effectiveVPosAfterShift,
    state.horizontalPosition
  );
  if (anchorAfterShift > 0) {
    state.highlightAnchorTimeMs = anchorAfterShift;
  }

  return true;
};

/**
 * Advances the guide time window to a new wall-clock boundary during auto-refresh.
 * Sets all three window anchors (past, current, future) aligned to the new
 * boundary and resets horizontal focus to the first program slot — matching
 * the forward-shift focus pattern from applyShiftForward.
 *
 * Also re-anchors guideSessionStartMs to this boundary: this only runs while
 * the guide is parked on the On Now window (see the guards in
 * autoRefreshGuideWindow), so the boundary it's snapping to *is* the new live
 * "now". While browsing away from On Now (past or future) the window is left
 * alone, but the anchor still needs to keep tracking true "now" — see
 * applyGuideSessionAnchorRefresh, dispatched separately for that case.
 *
 * @param state Mutable guide slice draft.
 * @param newBoundaryMs New boundary epoch milliseconds (snapped to interval).
 */
export const applyAutoRefreshAdvance = (
  state: Draft<GuideState>,
  newBoundaryMs: number
): void => {
  state.currentWindowStartTime = new DateTime(newBoundaryMs);
  state.pastWindowStartTime = new DateTime(newBoundaryMs).subtractMinutes(
    GUIDE_WINDOW_DURATION_MINUTES
  );
  state.futureWindowStartTime = new DateTime(newBoundaryMs).addMinutes(
    GUIDE_WINDOW_DURATION_MINUTES
  );
  state.horizontalPosition = 1;
  state.guideSessionStartMs = newBoundaryMs;
};

/**
 * Refreshes only the live-time session anchor (guideSessionStartMs) to the
 * given wall-clock boundary, without touching the visible window.
 *
 * autoRefreshGuideWindow's window-snap guards intentionally skip moving the
 * window while the user is browsing past or future content — but the anchor
 * itself must still keep tracking true "now" every tick, or a guide session
 * left idle for hours while parked in the past/future goes stale: the next
 * forward/backward shift would measure distance-to-live against a boundary
 * that's hours out of date instead of the real elapsed time.
 *
 * @param state Mutable guide slice draft.
 * @param newBoundaryMs New boundary epoch milliseconds (snapped to interval).
 */
export const applyGuideSessionAnchorRefresh = (
  state: Draft<GuideState>,
  newBoundaryMs: number
): void => {
  state.guideSessionStartMs = newBoundaryMs;
};

/**
 * Promotes rows backward (past→current, current→future) and rewinds the
 * currentWindowStartTime by GUIDE_SHIFT_MINUTES.
 * Sets isPastGuide = true on first backward shift.
 * Returns true if the shift was applied; false if the past boundary was hit.
 *
 * @param state Mutable guide slice draft.
 * @param epgLookBackHours Maximum hours backward navigation is allowed.
 * @returns Whether the shift was applied.
 */
export const applyShiftBackward = (
  state: Draft<GuideState>,
  epgLookBackHours: number
): boolean => {
  if (
    state.currentWindowStartTime === null ||
    state.guideSessionStartMs === 0
  ) {
    return false;
  }
  const currentTimeMs: number = state.currentWindowStartTime.getTime();
  const windowDurationMs: number = minutesToMilliseconds(
    GUIDE_WINDOW_DURATION_MINUTES
  );
  const shiftDurationMs: number = minutesToMilliseconds(GUIDE_SHIFT_MINUTES);
  const pastLimitMs: number = getPastLimitMs(
    state.guideSessionStartMs,
    epgLookBackHours
  );

  if (currentTimeMs <= pastLimitMs) {
    return false;
  }

  const nextTimeMs: number = currentTimeMs - shiftDurationMs;
  const nextWindowStartTime =
    state.currentWindowStartTime.subtractMinutes(GUIDE_SHIFT_MINUTES);
  state.currentWindowStartTime = nextWindowStartTime;

  const previousBucketIndex: number = getBucketIndex(
    currentTimeMs,
    state.guideSessionStartMs,
    windowDurationMs
  );
  const nextBucketIndex: number = getBucketIndex(
    nextTimeMs,
    state.guideSessionStartMs,
    windowDurationMs
  );

  if (nextBucketIndex < previousBucketIndex) {
    rotateBucketsBackward(state);
  }

  // Every shift, not just a bucket rotation — see syncWindowAnchorsToCurrent.
  syncWindowAnchorsToCurrent(state);

  state.isPastGuide = nextWindowStartTime.getTime() < state.guideSessionStartMs;

  const effectiveVPos: number = getEffectiveVerticalPosition(state);
  const maxHPos: number = getMaxHorizontalPosition(state, effectiveVPos);
  state.horizontalPosition = maxHPos > 0 ? maxHPos : 0;

  // Refresh time anchor from the new focused cell after shift
  if (state.horizontalPosition > 0) {
    const anchorAfterShift: number = getFocusedCellStartMs(
      state,
      effectiveVPos,
      state.horizontalPosition
    );
    if (anchorAfterShift > 0) {
      state.highlightAnchorTimeMs = anchorAfterShift;
    }
  }

  return true;
};

// ─── Async Prefetch Helper ────────────────────────────────────────────────────

/**
 * Fetches guide rows for a pre-computed prefetch target from the cache.
 * Returns null if there are no channels or no prefetch target — callers should
 * treat null as "nothing to do".
 *
 * The caller is responsible for dispatching `setGuideRowsDirect` with the
 * returned rows, and for catching any thrown errors.
 *
 * @param prefetchTarget Pre-computed window+bucket descriptor, or null.
 * @param channels Active channel list.
 * @returns Fetched guide rows, or null if skipped.
 */
export const fetchPrefetchRows = async (
  prefetchTarget: GuidePrefetchTarget | null,
  channels: TChannel[]
): Promise<TGridGuideRow[] | null> => {
  if (channels.length === 0 || prefetchTarget === null) {
    return null;
  }
  return GuideCache.getInstance().fetch(
    prefetchTarget.windowStartTime,
    channels
  );
};

/**
 * Determines whether a prefetch is needed and which window to fetch.
 *
 * @param state Current guide state snapshot.
 * @param direction Side to prefetch.
 * @returns Prefetch plan when missing data exists; otherwise null.
 */
export const getPrefetchPlan = (
  state: GuideState,
  direction: GuideTimeWindow
): GuidePrefetchTarget | null => {
  if (direction === GuideTimeWindow.FUTURE) {
    if (
      state.guideRows.futureRows.length > 0 ||
      state.futureWindowStartTime === null
    ) {
      return null;
    }

    return {
      windowStartTime: state.futureWindowStartTime,
      timeWindow: GuideTimeWindow.FUTURE
    };
  }

  if (
    state.guideRows.pastRows.length > 0 ||
    state.pastWindowStartTime === null
  ) {
    return null;
  }

  return {
    windowStartTime: state.pastWindowStartTime,
    timeWindow: GuideTimeWindow.PAST
  };
};

/**
 * Resolves prefetch rows for the requested direction from current guide state.
 *
 * @param state Current guide state snapshot.
 * @param direction Side to prefetch.
 * @returns Prefetched rows + target timeWindow, or null when no prefetch is needed.
 */
export const resolvePrefetchRows = async (
  state: GuideState,
  direction: GuideTimeWindow
): Promise<{
  rows: TGridGuideRow[];
  timeWindow: GuideTimeWindow;
  startGlobalIndex: number;
} | null> => {
  const prefetchTarget: GuidePrefetchTarget | null = getPrefetchPlan(
    state,
    direction
  );

  const window: GuideWindowChannels = getGuideWindowChannels(state);
  const rows: TGridGuideRow[] | null = await fetchPrefetchRows(
    prefetchTarget,
    window.channels
  );

  if (rows === null || prefetchTarget === null) {
    return null;
  }

  return {
    rows,
    timeWindow: prefetchTarget.timeWindow,
    startGlobalIndex: window.startGlobalIndex
  };
};

/**
 * Determines whether right navigation should shift the time axis forward.
 *
 * @param preHorizontalPosition Horizontal position before right step.
 * @param postHorizontalPosition Horizontal position after right step.
 * @returns True when the step stalled at row end and a shift is needed.
 */
export const shouldShiftForwardAfterRightStep = (
  preHorizontalPosition: number,
  postHorizontalPosition: number
): boolean => preHorizontalPosition === postHorizontalPosition;

/**
 * Determines whether the non-present-window guard should trigger on left navigation.
 *
 * @param preHorizontalPosition Horizontal position before left step.
 * @param postHorizontalPosition Horizontal position after left step.
 * @param preWindowStartTime Window start before left step.
 * @param sessionStartMs Session-start anchor in milliseconds.
 * @returns True when left move should shift backward instead of entering channel cell.
 */
export const shouldTriggerNonPresentWindowGuardOnLeft = (
  preHorizontalPosition: number,
  postHorizontalPosition: number,
  preWindowStartTime: DateTime | null,
  sessionStartMs: number
): boolean => {
  return (
    preHorizontalPosition === 1 &&
    postHorizontalPosition === 0 &&
    preWindowStartTime !== null &&
    sessionStartMs > 0 &&
    preWindowStartTime.getTime() !== sessionStartMs
  );
};

/**
 * Determines whether a window is the immediate past window — the one
 * bordering the current/session window — as opposed to a window further
 * back in the past.
 *
 * The channel cell and filter sidebar in past guide should only be
 * reachable from this window: one GUIDE_SHIFT_MINUTES step away from
 * `sessionStartMs`. Any deeper past window should keep shifting forward
 * on a RIGHT stall instead of exposing the channel cell.
 *
 * @param currentWindowStartTime Window start of the window being checked.
 * @param sessionStartMs Session-start anchor in milliseconds.
 * @returns True when the window is exactly one shift behind session start.
 */
export const isImmediatePastWindow = (
  currentWindowStartTime: DateTime | null,
  sessionStartMs: number
): boolean => {
  if (currentWindowStartTime === null || sessionStartMs <= 0) {
    return false;
  }

  const shiftDurationMs: number = minutesToMilliseconds(GUIDE_SHIFT_MINUTES);
  return currentWindowStartTime.getTime() + shiftDurationMs === sessionStartMs;
};

/**
 * Determines whether a backward shift was blocked by the past boundary.
 *
 * @param preShiftWindowMs Window start time before attempting the shift.
 * @param postShiftWindowTime Window start time after attempting the shift.
 * @returns True when the shift did not advance because boundary was hit.
 */
export const isBackwardShiftBlockedAtBoundary = (
  preShiftWindowMs: number,
  postShiftWindowTime: DateTime | null
): boolean => {
  return (
    postShiftWindowTime !== null &&
    postShiftWindowTime.getTime() === preShiftWindowMs
  );
};

// ─── Jump-to-Channel Resolution Helpers ───────────────────────────────────────

/**
 * Return type for {@link computeJumpScrollTarget}.
 */
export interface JumpScrollTarget {
  channelScrollOffset: number;
  verticalPosition: number;
}

/** Target visible row for a jump (3rd row = index 2, zero-based). */
const JUMP_TARGET_ROW: number = 2;

/**
 * Builds the effective (possibly filtered) channel list for jump resolution.
 *
 * When any guide filter is active, only channels matching the filter union
 * are returned (see `matchesActiveGuideFilters`). Otherwise, all channels are
 * returned. Each entry pairs the channel with its position in the effective
 * list (effectiveIndex) so callers don't need to re-derive it.
 *
 * @param state Guide state snapshot (read-only or Draft).
 * @returns Effective channel list with effective indices.
 */
const buildEffectiveChannelsForJump = (
  state: GuideState | Draft<GuideState>
): Array<{ channel: TChannel; effectiveIndex: number }> => {
  if (isAnyGuideFilterActive(state)) {
    const result: Array<{ channel: TChannel; effectiveIndex: number }> = [];
    let effectiveIndex: number = 0;
    for (const channel of state.channels) {
      if (matchesActiveGuideFilters(state, channel)) {
        result.push({ channel, effectiveIndex });
        effectiveIndex++;
      }
    }
    return result;
  }

  return state.channels.map(
    (
      channel: TChannel,
      effectiveIndex: number
    ): { channel: TChannel; effectiveIndex: number } => ({
      channel,
      effectiveIndex
    })
  );
};

/**
 * Resolves the effective-list index for a channel number entered via
 * jump-to-channel digit entry.
 *
 * Resolution order (§4 of the Jump-to-Channel spec):
 * 1. **Exact string match** — first channel whose `channelNumber` equals `digits`.
 * 2. **Nearest numeric match** — parse `digits` as an integer; for each channel
 *    with a purely numeric `channelNumber`, compute `|channelNumber − target|`;
 *    return the channel with the smallest distance. Tie-breaker: lower number wins.
 *    Non-numeric channel numbers (e.g. `"2-1"`) are skipped.
 * 3. **Not found** — return `null` (non-numeric input with no exact match, or
 *    empty lineup).
 *
 * @param state Current guide state (read-only or Draft). The effective channel
 *              list respects the active filter (e.g. FAVORITE).
 * @param digits Accumulated digit string from the user (e.g. `"42"`, `"001"`).
 * @returns Effective-list index of the resolved channel, or `null`.
 */
export const resolveJumpChannelIndex = (
  state: GuideState | Draft<GuideState>,
  digits: string
): number | null => {
  const effective = buildEffectiveChannelsForJump(state);

  if (effective.length === 0 || digits.length === 0) {
    return null;
  }

  // 1. Exact string match (first occurrence).
  for (const entry of effective) {
    if (entry.channel.channelNumber === digits) {
      return entry.effectiveIndex;
    }
  }

  // 2. Nearest numeric fallback.
  const target: number = parseInt(digits, 10);
  if (isNaN(target)) {
    return null;
  }

  let bestIndex: number = -1;
  let bestDistance: number = Infinity;
  let bestNumber: number = Infinity;

  for (const entry of effective) {
    const chNum: string | undefined = entry.channel.channelNumber;
    if (chNum === undefined || chNum.length === 0) {
      continue;
    }
    // Skip non-numeric channel numbers (e.g. "2-1", "2.1").
    const parsed: number = parseInt(chNum, 10);
    if (isNaN(parsed) || String(parsed) !== chNum) {
      continue;
    }
    const distance: number = Math.abs(parsed - target);
    if (
      distance < bestDistance ||
      (distance === bestDistance && parsed < bestNumber)
    ) {
      bestDistance = distance;
      bestNumber = parsed;
      bestIndex = entry.effectiveIndex;
    }
  }

  if (bestIndex === -1) {
    return null;
  }

  return bestIndex;
};

/**
 * Computes the scroll-offset and vertical-position values that place the
 * channel at `effectiveIndex` on the target visible row (3rd row, index 2).
 *
 * When the effective channel count is small enough to fit entirely on screen,
 * the offset is clamped to 0 and `verticalPosition` is set to the channel's
 * actual index. Otherwise circular modulo arithmetic is used.
 *
 * @param effectiveIndex Index of the target channel in the effective list.
 * @param effectiveCount Total number of channels in the effective list.
 * @returns `{ channelScrollOffset, verticalPosition }` to apply to state.
 */
export const computeJumpScrollTarget = (
  effectiveIndex: number,
  effectiveCount: number
): JumpScrollTarget => {
  if (effectiveCount <= GUIDE_VISIBLE_ROW_COUNT) {
    // Lineup fits on screen — no wrapping; clamp offset to 0.
    return {
      channelScrollOffset: 0,
      verticalPosition: effectiveIndex
    };
  }

  const targetRow: number = Math.min(JUMP_TARGET_ROW, effectiveCount - 1);
  const offset: number =
    (((effectiveIndex - targetRow) % effectiveCount) + effectiveCount) %
    effectiveCount;

  return {
    channelScrollOffset: offset,
    verticalPosition: targetRow
  };
};
